import { enableProdMode } from '@angular/core';
import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';
import { Amplify } from 'aws-amplify';
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito';
import { CookieStorage } from 'aws-amplify/utils';

import { AppModule } from './app/app.module';
import { environment } from './environments/environment';
import awsconfigtrainee from './aws-exports-trainee.js';
import awsconfigmanagement from './aws-exports-management.js';

// --- TP2020-11188: cookie isolation ------------------------------------------
// Auth cookies are scoped host-only (app.amopportunities.org) so the trainee app
// and the admin app no longer share one .amopportunities.org cookie jar (the source
// of the "confused login" states). The one intentional cross-over — the manager
// "Open Program Search" hand-off — is carried by an explicit #h= URL fragment that
// an APP_INITIALIZER exchanges into this app's own host-only jar (see Phase 3).
const AUTH_COOKIE_NAMES = ['amoactkn', 'mgmt_token', 'mgmtlogin', 'amomgmt', '_bp_', '_temp_'];

function readRawCookie(name: string): string {
    if (typeof document === 'undefined') return '';
    const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const match = document.cookie.match(new RegExp('(?:^|; )' + escaped + '=([^;]*)'));
    return match ? decodeURIComponent(match[1]) : '';
}

// One-time migration: legacy builds wrote auth cookies on the shared parent domain
// (.amopportunities.org). Host-only setters can never delete those, so a stale
// parent amoactkn / Cognito key would linger and keep guards "logged in". Clear the
// legacy parent-domain auth cookies once per browser, before Amplify reads them.
// Analytics cookies (e.g. _seg_) are intentionally left untouched.
function purgeLegacyParentDomainAuthCookies(): void {
    if (typeof document === 'undefined') return;
    try {
        if (localStorage.getItem('amo_cookie_isolation_v1') === 'done') return;
    } catch (e) {
        /* localStorage unavailable — fall through and purge anyway */
    }
    const names = new Set<string>(AUTH_COOKIE_NAMES);
    document.cookie.split(';').forEach((c) => {
        const n = c.split('=')[0].trim();
        if (n.startsWith('CognitoIdentityServiceProvider.')) names.add(n);
    });
    ['.amopportunities.org', 'amopportunities.org'].forEach((domain) => {
        names.forEach((n) => {
            document.cookie = `${n}=; path=/; domain=${domain}; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
        });
    });
    try {
        localStorage.setItem('amo_cookie_isolation_v1', 'done');
    } catch (e) {
        /* ignore */
    }
}

purgeLegacyParentDomainAuthCookies();

// Pool selection. Default to the trainee pool. We no longer sniff the shared cookie
// jar for the admin pool's keys — the jars are isolated. Use the management pool
// only when this app has its own host-only management session (amo_app_pool flag,
// set by a prior #h= hand-off) or a hand-off is in flight on this load (#h= present,
// or the legacy ?mgmt=1 shim kept for one release).
const handoffInFlight = typeof window !== 'undefined' && /[#&]h=/.test(window.location.hash);
const mgmtUrlFlag = typeof window !== 'undefined' && /[?&]mgmt=1(?:&|$)/.test(window.location.search);
const appPoolIsManagement = readRawCookie('amo_app_pool') === 'management';
Amplify.configure(appPoolIsManagement || handoffInFlight || mgmtUrlFlag ? awsconfigmanagement : awsconfigtrainee);

// Store Cognito tokens host-only on this subdomain. Singleton storage; survives the
// dynamic Amplify.configure() pool switches the login/forgot/reset pages perform.
(() => {
    const host = typeof window !== 'undefined' ? window.location.hostname : '';
    const secure = typeof window !== 'undefined' && window.location.protocol === 'https:';
    cognitoUserPoolsTokenProvider.setKeyValueStorage(
        new CookieStorage({ domain: host, secure, sameSite: 'lax', expires: 30 })
    );
})();

if (environment.production) {
    enableProdMode();
}

// Used to properly register the SW
//https://stackoverflow.com/questions/50968902/angular-service-worker-swupdate-available-not-triggered

platformBrowserDynamic()
    .bootstrapModule(AppModule)
    .then(() => {
        if ('serviceWorker' in navigator && environment.production) {
            navigator.serviceWorker.register('ngsw-worker.js');
        }
        console.log('SW registered');
    })
    .catch((err) => console.log(err));
